import * as _ from "lodash";
import * as echarts from "echarts";
import { isNullOrUndefined } from "util";

import { PortfolioRepo as portfolioRepo } from "@services/portfolio.repo";
import { PortfolioUtilities as portfolioUtilities } from "@common/portfolio.utilities";
import { portfolioSelectorService } from "../portfolioSelector";
import {
  AssetClassNames,
  AssetClassColour,
  AssetClassColourArray,
} from "@common/enums/AssetClassType";

class PortfolioInternationalController implements angular.IOnInit {
  public assetClass: any;
  public assetClassesTotal: any;

  public equityTableShow: boolean = false;
  public fixedIncomeTableShow: boolean = false;
  public preciousMetalsTableShow: boolean = false;
  public alternativeInvestmentsTableShow: boolean = false;
  public cashTableShow: boolean = false;

  private assetClassPieChart: any;
  private assetClassPieChartOptions: any;

  private minScreenWidth: number = 400;
  // colour array
  public colourArray = AssetClassColourArray;

  /** @ngInject */
  constructor(
    private $scope: angular.IScope,
    private portfolioRepo: portfolioRepo,
    private portfolioUtilities: portfolioUtilities,
    private portfolioSelectorService: portfolioSelectorService,
    private notifyjs: any
  ) {}

  $onInit(): void {
    this.assetClassPieChart = echarts.init(
      document.getElementById("asset-class-pie-chart")
    );
    this.assetClassPieChart.on("click", this.onAssetChartClick.bind(this));

    this.portfolioSelectorService.registerCallback(
      "portfolio.international",
      this.updateData.bind(this)
    );
    this.updateData();
  }

  public getAssetClassPercentage(assetClass: any) {
    if (assetClass) {
      return (assetClass.total / this.assetClassesTotal) * 100;
    }
  }

  private onAssetChartClick(params) {
    this.assetClassTableSwitch(params.data.name);
    this.$scope.$apply();
  }

  public getAssetClassTotal(assetClassTotals, assetClassName) {
    let assetClassTotal = assetClassTotals.find(
      (assetClass) => assetClass.name == assetClassName
    );
    return isNullOrUndefined(assetClassTotal) ? 0 : assetClassTotal;
  }

  public assetClassTableSwitch(assetClassName: string) {
    this.preciousMetalsTableShow = false;
    this.fixedIncomeTableShow = false;
    this.equityTableShow = false;
    this.cashTableShow = false;
    this.alternativeInvestmentsTableShow = false;

    switch (assetClassName) {
      case AssetClassNames.EQUITY:
        this.equityTableShow = true;
        break;
      case AssetClassNames.FIXED_INCOME:
        this.fixedIncomeTableShow = true;
        break;
      case AssetClassNames.PRECIOUS_METALS:
        this.preciousMetalsTableShow = true;
        break;
      case AssetClassNames.ALTERNATIVE_INV:
        this.alternativeInvestmentsTableShow = true;
        break;
      case AssetClassNames.CASH:
        this.cashTableShow = true;
        break;
    }
  }

  public formatCurrency() {
    if (!this.portfolioSelectorService.isLoading) {
      return this.portfolioUtilities.formatCurrency(
        this.portfolioSelectorService.getSelectedCurrency()
      );
    } else {
      return "";
    }
  }

  public formatName(name: string) {
    return _.startCase(name);
  }

  updateData() {
    this.portfolioSelectorService.setIsLoading(true);
    // Asset Class Data
    this.portfolioRepo
      .getPortfolioAggregatesByAssetClasses(
        null,
        this.portfolioSelectorService.getSelectedCurrency(),
        "INTERNATIONAL",
        this.portfolioSelectorService.getSelectedPortfolios()
      )
      .then((val) => {
        this.assetClass = val as Array<any>;
        this.assetClassesTotal = this.assetClass.reduce(
          (prev, next) => prev + next.total,
          0
        );

        this.assetClassPieChartOptions = {
          tooltip: {
            trigger: "item",
            formatter: "{b} : {d}%",
          },
          grid: {
            containLabel: true,
          },
          series: {
            type: "pie",
            radius: ["40%", "60%"],
            center: ["50%", "50%"],
            labelLine: {
              normal: {
                show: window.screen.width > this.minScreenWidth,
              },
            },
            label: {
              normal: {
                show: window.screen.width > this.minScreenWidth,
                fontSize: window.screen.width > 1920 ? 24 : 12,
              },
            },
            data: [
              {
                name: AssetClassNames.EQUITY,
                value: this.getAssetClassPercentage(
                  this.getAssetClassTotal(
                    this.assetClass,
                    AssetClassNames.EQUITY
                  )
                ),
                itemStyle: {
                  normal: {
                    color: AssetClassColour.EQUITY,
                    fontFamily: "Helvetica",
                  },
                },
              },
              {
                name: AssetClassNames.FIXED_INCOME,
                value: this.getAssetClassPercentage(
                  this.getAssetClassTotal(
                    this.assetClass,
                    AssetClassNames.FIXED_INCOME
                  )
                ),
                itemStyle: {
                  normal: {
                    color: AssetClassColour.FIXED_INCOME,
                    fontFamily: "Helvetica",
                  },
                },
              },
              {
                name: AssetClassNames.PRECIOUS_METALS,
                value: this.getAssetClassPercentage(
                  this.getAssetClassTotal(
                    this.assetClass,
                    AssetClassNames.PRECIOUS_METALS
                  )
                ),
                itemStyle: {
                  normal: {
                    color: AssetClassColour.PRECIOUS_METALS,
                    fontFamily: "Helvetica",
                  },
                },
              },
              {
                name: AssetClassNames.ALTERNATIVE_INV,
                value: this.getAssetClassPercentage(
                  this.getAssetClassTotal(
                    this.assetClass,
                    AssetClassNames.ALTERNATIVE_INV
                  )
                ),
                itemStyle: {
                  normal: {
                    color: AssetClassColour.ALTERNATIVE_INV,
                    fontFamily: "Helvetica",
                  },
                },
              },
              {
                name: AssetClassNames.CASH,
                value: this.getAssetClassPercentage(
                  this.getAssetClassTotal(this.assetClass, AssetClassNames.CASH)
                ),
                itemStyle: {
                  normal: {
                    color: AssetClassColour.CASH,
                    fontFamily: "Helvetica",
                  },
                },
              },
            ],
          },
        };

        this.assetClassPieChartOptions.series.data =
          this.assetClassPieChartOptions.series.data.filter(
            (item) => item.value > 0
          );
        this.assetClassPieChart.setOption(this.assetClassPieChartOptions);
      })
      .finally(() => this.portfolioSelectorService.setIsLoading(false));
  }
}

export const portfolioInternational: angular.IComponentOptions = {
  template: require("../portfolio.international/portfolio.international.html"),
  controller: PortfolioInternationalController,
};



// WEBPACK FOOTER //
// ./src/app/portfolio/portfolio.international/portfolio.international.ts